import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Eye, Plus, Search, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CustomerGroupSelect } from "@/components/shared/CustomerGroupSelect";
import { ErrorState } from "@/components/shared/ErrorState";
import { EmptyState } from "@/pages/CustomerPortal/Financial/component/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import { physicalClientsApi, PhysicalClientStatusFilter } from "@/lib/api";
import { formatPhone } from "@/lib/formatters";
import { normalizeCpf } from "@/lib/validators/cpf";
import { PAGE_SIZES } from "../CustomerGroups/component/pagination";
import { TablePagination } from "../CustomerGroups/component/TablePagination";
import { PhysicalClientStatusBadge } from "./component/PhysicalClientStatusBadge";
import { STATUS_FILTER_LABELS } from "./component/physicalClientLabels";
import { buildSearchBody, buildSearchQueryKey, resolveSearchMode } from "./physicalClientSearch";

/**
 * Backoffice > Clientes > Pessoa física: busca paginada na API. Um único campo busca por CPF (exato, se o texto for um
 * CPF válido) ou por nome, e-mail e código ERP. O texto da busca não vai para a URL, para o localStorage nem para a
 * chave da query quando é um CPF; o CPF vai só no body do POST.
 */
export default function PhysicalClientsList() {
  const navigate = useNavigate();
  const searchId = useId();
  const statusId = useId();
  const groupId = useId();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<PhysicalClientStatusFilter>("Active");
  const [customerGroupId, setCustomerGroupId] = useState<number | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[1]);
  const debouncedSearch = useDebounce(search, 300);
  const mode = resolveSearchMode(debouncedSearch);

  // Número que muda a cada CPF diferente buscado: entra na chave da query no lugar do CPF.
  const cpfSearch = useRef({ digits: "", id: 0 });
  const cpfSearchId = useMemo(() => {
    if (mode !== "cpf") return 0;
    const digits = normalizeCpf(debouncedSearch);
    if (digits !== cpfSearch.current.digits) cpfSearch.current = { digits, id: cpfSearch.current.id + 1 };
    return cpfSearch.current.id;
  }, [mode, debouncedSearch]);

  const filters = { status, customerGroupId };

  const query = useQuery({
    queryKey: buildSearchQueryKey(debouncedSearch, filters, page, pageSize, cpfSearchId),
    queryFn: () => physicalClientsApi.search(buildSearchBody(debouncedSearch, filters, page, pageSize)),
    placeholderData: keepPreviousData,
  });

  // Nova busca ou filtro: volta para a primeira página.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status, customerGroupId, pageSize]);

  const clients = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <UserRound className="h-6 w-6 text-primary" aria-hidden="true" />
            <h1 className="text-2xl font-bold tracking-tight">Clientes pessoa física</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Cadastro de clientes com CPF. O CPF aparece mascarado; o completo só pela ação "Ver CPF" no cadastro, que fica
            registrada.
          </p>
        </div>
        <Button asChild>
          <Link to="/backoffice/clientes/pf/novo">
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Novo cliente PF
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader className="grid gap-4 space-y-0 md:grid-cols-[2fr_1fr_1fr]">
          <div className="space-y-1">
            <Label htmlFor={searchId}>Buscar</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                id={searchId}
                type="search"
                autoComplete="off"
                className="pl-9"
                placeholder="Nome, e-mail, código ERP ou CPF completo"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {mode === "cpf" && (
              <p className="text-xs text-muted-foreground">Busca por CPF: considera todas as situações.</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor={statusId}>Situação</Label>
            <Select value={status} onValueChange={(value) => setStatus(value as PhysicalClientStatusFilter)}>
              <SelectTrigger id={statusId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(STATUS_FILTER_LABELS) as PhysicalClientStatusFilter[]).map((value) => (
                  <SelectItem key={value} value={value}>
                    {STATUS_FILTER_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor={groupId}>Grupo</Label>
            <CustomerGroupSelect
              id={groupId}
              value={customerGroupId ?? null}
              onChange={setCustomerGroupId}
              onClear={() => setCustomerGroupId(undefined)}
              clearLabel="Todos os grupos"
              ariaLabel="Filtrar por grupo"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {query.isLoading ? (
            <div className="space-y-2" aria-busy="true">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : query.isError ? (
            <ErrorState message="Não foi possível carregar os clientes." onRetry={() => void query.refetch()} />
          ) : clients.length === 0 ? (
            <EmptyState title="Nenhum cliente encontrado" message="Revise a busca ou os filtros." className="py-8" />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>CPF</TableHead>
                      <TableHead>E-mail</TableHead>
                      <TableHead>Celular</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow
                        key={client.id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/backoffice/clientes/pf/${client.id}`)}
                      >
                        <TableCell className="font-medium">
                          <Link
                            to={`/backoffice/clientes/pf/${client.id}`}
                            className="hover:underline"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {client.fullName}
                          </Link>
                        </TableCell>
                        <TableCell className="font-mono">{client.cpfMasked}</TableCell>
                        <TableCell>{client.email || "—"}</TableCell>
                        <TableCell className="whitespace-nowrap">{formatPhone(client.mobilePhone) || "—"}</TableCell>
                        <TableCell>{client.customerGroup?.name ?? "—"}</TableCell>
                        <TableCell>
                          <PhysicalClientStatusBadge status={client.status} />
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end">
                            <Button variant="ghost" size="sm" asChild>
                              <Link
                                to={`/backoffice/clientes/pf/${client.id}`}
                                aria-label={`Visualizar ${client.fullName}`}
                                onClick={(event) => event.stopPropagation()}
                              >
                                <Eye className="mr-1 h-4 w-4" aria-hidden="true" />
                              </Link>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <TablePagination
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                disabled={query.isPlaceholderData}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
